import type { LengthUnit, WeightUnit } from "../types/models";
import { lengthToCm, weightToKg } from "./units";

type HydrationReference = "female" | "male";

export type HydrationPreviewInput = {
  height: number | null;
  heightUnit: LengthUnit;
  birthYear: number | null;
  hydrationReference: HydrationReference | null;
  weight?: number | null;
  weightUnit: WeightUnit;
};

export type HydrationPreview = {
  goalMl: number;
  source: "local-preview";
  formulaVersion: string;
};

const DRINK_BASELINES_ML: Record<HydrationReference, number> = {
  female: 1800,
  male: 2050
};

const REFERENCE_HEIGHT_CM: Record<HydrationReference, number> = {
  female: 162,
  male: 175
};

const REFERENCE_WEIGHT_KG: Record<HydrationReference, number> = {
  female: 65,
  male: 78
};

export function buildHydrationPreview(input: HydrationPreviewInput, currentYear = new Date().getFullYear()): HydrationPreview | null {
  if (!input.height || !input.birthYear || !input.hydrationReference) {
    return null;
  }

  const heightCm = lengthToCm(input.height, input.heightUnit);
  const age = currentYear - input.birthYear;
  if (!Number.isFinite(heightCm) || heightCm <= 0 || age < 0 || age > 130) {
    return null;
  }

  const reference = input.hydrationReference;
  const heightAdjustment = clamp(heightCm - REFERENCE_HEIGHT_CM[reference], -30, 35) * 2;
  const weightKg = input.weight ? weightToKg(input.weight, input.weightUnit) : null;
  const weightAdjustment = weightKg ? clamp(weightKg - REFERENCE_WEIGHT_KG[reference], -25, 30) * 4 : 0;
  const rawGoal = DRINK_BASELINES_ML[reference] + heightAdjustment + weightAdjustment;

  return {
    goalMl: clamp(roundToNearest(rawGoal, 50), 1400, 3000),
    source: "local-preview",
    formulaVersion: "drink-target-v3"
  };
}

function roundToNearest(value: number, step: number): number {
  return Math.round(value / step) * step;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
